import _ from 'underscore';
import ko from 'knockout';
import Promise from 'bluebird';
import Backbone from 'backbone';
import pgrid from 'projection-grid';

import { paginationPlugin } from '@bingads-webui/projection-grid-pagination';
import { uiBlockerPlugin } from '@bingads-webui/projection-grid-uiblocker';

import scheduledImportPopupTmpl from '../template/scheduled-import-popup.pug';

function ScheduledImportPopupViewModel() {
  this.showGrid = ko.observable(true);
}

export class ScheduledImportPopup extends Backbone.View {
  initialize({ aid, deps }) {
    this.aid = aid;
    this.viewModel = new ScheduledImportPopupViewModel();

    this.i18n = deps.i18n;
    this.i18nGlobal = deps.i18nGlobal;
    this.importTaskItemSchema = deps.importTaskItemSchema;
    this.importStatusColumnTmpl = deps.importStatusColumnTmpl;
    this.importSchedNameTmpl = deps.importSchedNameTmpl;
    this.importCreatorColumnTmpl = deps.importCreatorColumnTmpl;
    this.idTmpl = deps.idTmpl;
    this.constants = deps.constants;
    this.importFreqColDataToTemplate = deps.importFreqColDataToTemplate;
    this.edm = deps.edm;
    this.jsdataCampaign = deps.jsdataCampaign;
    this.config = deps.config;
    this.dateUtil = deps.dateUtil;
    this.permissions = deps.config.get('Permissions') || {};
  }

  initTemplate() {
    this.$el.html(scheduledImportPopupTmpl({ i18n: this.i18n }));
    ko.applyBindings(this.viewModel, this.el);
  }

  initGridView() {
    const { i18n, permissions } = this;
    const { gridView } = pgrid.factory({ vnext: true })
      .use(paginationPlugin)
      .use(uiBlockerPlugin)
      .create({
        name: 'ScheduledImportPopupGrid',
        el: this.$('.grid'),
        tableClasses: ['table', 'table-bordered'],
        dataSource: {
          type: 'js-data',
          entity: {
            findAll: (...args) => this.jsdataCampaign.TaskItem.findAll(...args)
              .then((data) => {
                this.viewModel.showGrid(data.totalCount > 0);
                return data;
              }),
          },
          schema: this.importTaskItemSchema,
          query: {
            $filter: this.constants.FILTER_ALL_BUT_DELETED,
          },
          options: {
            entitySet: this.edm.currentCustomer.Accounts.$withKey(this.aid).TaskItems,
          },
        },
        columns: _.extend({}, {
          State: {
            name: 'State',
            title: i18n.getString('AutomatedRules_RulesManagementGrid_Status'),
            property: 'State',
            template: ({ value }) => this.importStatusColumnTmpl({ Status: value, noHover: true }),
            sortable: false,
          },
        }, permissions && permissions.dynamic && permissions.dynamic.ImportPreferenceUpload && {
          Id: {
            name: 'Id',
            title: i18n.getString('Scheduled_Import_Task_ID'),
            property: 'Id',
            template: ({ value }) => this.idTmpl({ id: value }),
            sortable: false,
            type: 'number',
          },
        }, {
          Name: {
            name: 'Name',
            title: i18n.getString('Scheduled_Import_Name'),
            property: 'Name',
            template: ({ value }) => this.importSchedNameTmpl({ Name: value || this.i18nGlobal.getString('QualityScore_NA') }),
            sortable: false,
            type: 'string',
          },
          User: {
            name: 'User',
            title: i18n.getString('AutomatedRules_RulesManagementGrid_CreatedBy'),
            property: {
              get: _.identity,
              keys: ['ModifiedBy', 'ModifiedAt'],
            },
            template: ({ value }) => this.importCreatorColumnTmpl({
              Creator: value.ModifiedBy && value.ModifiedBy.Name,
              DateTime: this.dateUtil.format(new Date(value.ModifiedAt), 'dddd, MMMM d, yyyy', this.config.get('culture')),
              Time: this.dateUtil.format(new Date(value.ModifiedAt), 'hh:mm:ss tt', this.config.get('culture')),
            }),
            sortable: false,
            type: 'string',
          },
          Scope: {
            name: 'Scope',
            title: i18n.getString('ColumnName_ImportScope'),
            property: {
              get({ EntityIdsSpecified }) {
                return i18n.getString(`LastImportScope_${EntityIdsSpecified ? 'Specific' : 'All'}`);
              },
              key: ['EntityIdsSpecified'],
            },
            sortable: false,
            type: 'string',
          },
          Frequency: {
            name: 'Frequency',
            title: i18n.getString('AutomatedRules_RuleAuthoring_Filter'),
            property: {
              get: _.identity,
              keys: ['Cron', 'TimeZoneId', 'IsAutoFrequency'],
            },
            template: ({ value }) => this.importFreqColDataToTemplate(value, i18n),
          },
        }),
        plugins: {
          pagination: {
            pageSize: 5,
            availablePageSizes: [5],
          },
        },
      });

    this.gridView = gridView;
  }

  render() {
    this.initTemplate();
    this.initGridView();
    this.gridView.render();

    return this;
  }

  renderUntilGridRendered() {
    return new Promise((resolve) => {
      this.initTemplate();
      this.initGridView();
      this.gridView.once('didReload', () => {
        this.gridView.once('didUpdate', () => {
          resolve();
        });
      });
      this.gridView.render();
    });
  }
}
